import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, ArrowRight, Check, MapPin, Smartphone, CloudHail, BadgeCheck,
  ShieldCheck, Paintbrush, PiggyBank, Timer, Clock,
} from "lucide-react";
import { Reveal, SectionHeading } from "../components/Shared";
import { PageHeader } from "./PaintlessDentRemoval";
import { CtaBanner } from "./Home";
import Seo from "../components/Seo";
import { business } from "../mock";
import { servicePages, serviceLinks } from "../data/servicePages";

const iconMap = {
  MapPin, Smartphone, CloudHail, BadgeCheck, ShieldCheck, Paintbrush, PiggyBank, Timer, Clock,
};

const ServicePage = ({ slug }) => {
  const data = servicePages[slug];
  if (!data) return null;

  const canonicalPath = `/${slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: data.h1,
        serviceType: data.crumb,
        provider: { "@type": "AutoRepair", name: "Dentcraft NSW", "@id": "https://dentcraftnsw.au/#business" },
        areaServed: ["Sydney", "Western Sydney", "Sydney Metro"],
        url: `https://dentcraftnsw.au${canonicalPath}`,
        description: data.seoDescription,
      },
      {
        "@type": "FAQPage",
        mainEntity: data.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  const related = serviceLinks.filter((l) => l.path !== canonicalPath);

  return (
    <div>
      <Seo title={data.seoTitle} description={data.seoDescription} path={canonicalPath} schema={schema} />

      <PageHeader title={data.h1} crumb={data.crumb} image={data.heroImage} />

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img
              src={data.intro.image}
              alt={data.intro.imageAlt}
              className="rounded-2xl shadow-xl w-full h-[420px] object-cover"
            />
          </Reveal>
          <Reveal delay={120}>
            <span className="text-[#84bd00] font-semibold text-sm tracking-widest uppercase">{data.intro.eyebrow}</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#242424] leading-tight">
              {data.intro.heading}
            </h2>
            {data.intro.paragraphs.map((p, i) => (
              <p key={i} className="mt-5 text-gray-600 text-lg leading-relaxed">{p}</p>
            ))}
            <ul className="mt-6 space-y-3">
              {data.intro.bullets.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-[#84bd00] flex items-center justify-center mt-0.5">
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="text-gray-700">{t}</span>
                </li>
              ))}
            </ul>
            <a href={`tel:${business.phoneRaw}`} className="mt-8 inline-flex items-center gap-2 bg-[#84bd00] hover:bg-[#75a800] text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              <Phone size={18} /> {business.phone}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#f7f9f2]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Why Dentcraft NSW" title={`What our ${data.crumb.toLowerCase()} service includes`} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.features.map((f, i) => {
              const Icon = iconMap[f.icon] || BadgeCheck;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="bg-white rounded-2xl p-7 h-full shadow-sm hover:shadow-lg transition-shadow">
                    <div className="w-14 h-14 rounded-2xl bg-[#84bd00]/10 flex items-center justify-center mb-5">
                      <Icon size={26} className="text-[#84bd00]" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#242424] mb-2">{f.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{f.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="FAQs" title="Frequently asked questions" />
          <div className="space-y-4">
            {data.faqs.map((f, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="border border-gray-200 rounded-xl p-6 hover:border-[#84bd00]/40 transition-colors">
                  <h3 className="font-display font-bold text-lg text-[#242424] mb-2">{f.q}</h3>
                  <p className="text-gray-600 leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related services (internal links) */}
      <section className="py-16 bg-[#242424]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-2xl text-white text-center mb-8">Related dent repair services in Sydney</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {related.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-[#84bd00] border border-white/15 text-gray-100 font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                {l.label} <ArrowRight size={16} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default ServicePage;
